import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AlertCircle } from "lucide-react";

/**
 * Mandatory password-change dialog shown immediately after login when the
 * server flag `must_change_password=true` (e.g. fresh admin seed). The user
 * cannot dismiss this dialog until they set a new password.
 */
export default function ForcePasswordChange() {
  const { mustChangePassword, clearMustChange, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  if (!mustChangePassword) return null;

  const save = async () => {
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    if (next === current) { toast.error("Choose a password different from the current one"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated — welcome!");
      clearMustChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={() => { /* not dismissable */ }}>
      <DialogContent
        className="max-w-md"
        data-testid="force-change-password-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-[#C0392B]" />
            Set a new password
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            For security, you must change the temporary password before continuing.
          </div>
          <div>
            <Label>Current (temporary) password</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="fcp-current" />
          </div>
          <div>
            <Label>New password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="fcp-new" />
            <div className="text-xs text-slate-500 mt-1">Minimum 6 characters.</div>
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="fcp-confirm" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={logout}
            className="border-slate-200"
            data-testid="fcp-signout"
          >
            Sign out
          </Button>
          <Button
            onClick={save}
            disabled={saving || !current || !next || !confirm}
            className="bg-[#2E86C1] hover:bg-[#2471A3] text-white"
            data-testid="fcp-save"
          >
            {saving ? "Saving…" : "Update & continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
